// ─────────────────────────────────────────
// brandLockup.ts — Assinatura de marca (BrandLockup)
// Tauvior — Hardware, sistemas embarcados e conectividade industrial
// ─────────────────────────────────────────
//
// Logo oficial Tauvior (símbolo + wordmark, o mesmo PNG do header, sem o
// slogan) + traço vertical + o nome da linha. Grid e Advisory são linhas da
// Tauvior: nunca ganham símbolo, cor de logo ou tipografia de marca própria.
//
// O logo é desenhado como máscara do PNG e pintado com `currentColor`; por
// isso o nome da linha tem sempre a mesma cor do wordmark, seja o branco
// sobre fundo escuro, seja o azul-marinho sobre o hero claro. Proporções e
// alinhamento ficam em pages.css (`.brand-lockup`).

export type BrandLine = 'grid' | 'advisory';

const LINE_NAMES: Record<BrandLine, string> = {
  grid:     'Grid',
  advisory: 'Advisory',
};

export interface BrandLockupProps {
  /** Sem `line`, a assinatura é só o logo Tauvior. */
  line?: BrandLine | undefined;
  /**
   * Só o símbolo Tauvior, sem o wordmark. Para tamanhos pequenos (≈16px),
   * em que o wordmark do PNG reduzido perde a nitidez.
   */
  markOnly?: boolean;
}

export function isBrandLine(value: string | undefined): value is BrandLine {
  return value === 'grid' || value === 'advisory';
}

/**
 * Devolve o HTML da assinatura. Para leitor de tela ela é uma imagem só,
 * com o nome completo ("Tauvior Grid") — as partes visuais ficam ocultas.
 */
export function BrandLockup({ line, markOnly = false }: BrandLockupProps = {}): string {
  const name  = line ? LINE_NAMES[line] : '';
  const label = name ? `Tauvior ${name}` : 'Tauvior';

  const linePart = name
    ? `<span class="brand-lockup-rule"></span><span class="brand-lockup-line">${name}</span>`
    : '';

  return `<span class="brand-lockup${markOnly ? ' is-mark' : ''}" role="img" aria-label="${label}"${line ? ` data-line="${line}"` : ''}>`
    + `<span class="brand-lockup-logo" aria-hidden="true"></span>`
    + (linePart ? `<span class="brand-lockup-name" aria-hidden="true">${linePart}</span>` : '')
    + `</span>`;
}

/**
 * Preenche os `[data-brand-lockup]` do HTML. O valor do atributo é a linha
 * (`grid`, `advisory`); vazio, é a assinatura da empresa.
 */
export function renderBrandLockups(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-brand-lockup]').forEach((el) => {
    const value = el.dataset.brandLockup;
    el.innerHTML = BrandLockup({ line: isBrandLine(value) ? value : undefined });
  });
}
